import SettingsStore from "@/stores/SettingsStore";
import { PHONE_REGEX } from "./constants";
import i18next from "@/i18n/config";

/** Phonetische Schreibweisen für die Avatar-Aussprache (Plain-Text, analog zu Parloa-Hints). */
const PRONUNCIATION_LEXICON: Record<string, string> = {
  DOMCURA: "Domkura",
};

export default function normalizeMessage(message: string): string {
  let newMessage = message.trim();
  newMessage = normalizeIban(newMessage);
  newMessage = normalizePhoneNumber(newMessage);
  newMessage = normalizeEmail(newMessage);
  newMessage = normalizeUrl(newMessage);
  newMessage = normalizeNewLine(newMessage);
  newMessage = applyPronunciationHints(newMessage);
  return newMessage;
}

export function applyPronunciationHints(message: string): string {
  let result = message;
  for (const [word, spoken] of Object.entries(PRONUNCIATION_LEXICON)) {
    result = result.replace(new RegExp(`\\b${word}\\b`, "gi"), spoken);
  }
  return result;
}

export function normalizeNewLine(message: string): string {
  return message.trim().replace(/\n/g, " ");
}

export function normalizeIban(message: string): string {
  
  const ibanRegex = /[A-Z]+\d{20}/g;

  if (ibanRegex.test(message)) {
    return message.replace(ibanRegex, iban => formatIban(iban));
  }

  return message;
}

export function formatIban(iban: string): string {
  const newMessage = iban.trim();
  if (newMessage.length < 2) return newMessage;

  // Split the country code into separate letters ending with a dot
  const countryCode = `${newMessage.slice(0, 2).split("").join(".")}.`;

  // Split the remainder of the IBAN into individual digits
  const digits = newMessage.slice(2).split("");

  // Join each digit with ". " and add a trailing dot
  return `${countryCode} ${digits.join(". ")}.`;
}

/**
 * Converts a digit to its word representation
 * @param digit - The digit character to convert
 * @returns The word representation of the digit
 */
function digitToWord(digit: string): string {
  const lng = SettingsStore.getState().language || "de";

  // Try to use i18next first
  const digitWord = i18next.t(`digits.${digit}`, { lng });

  // If translation is not found (returns the key), use fallback mapping
  if (digitWord === `digits.${digit}`) {
    // Fallback mappings
    const germanDigits: { [key: string]: string } = {
      "0": "null",
      "1": "eins",
      "2": "zwei",
      "3": "drei",
      "4": "vier",
      "5": "fünf",
      "6": "sechs",
      "7": "sieben",
      "8": "acht",
      "9": "neun",
      "+": "plus"
    };
    
    const englishDigits: { [key: string]: string } = {
      "0": "zero",
      "1": "one",
      "2": "two",
      "3": "three",
      "4": "four",
      "5": "five",
      "6": "six",
      "7": "seven",
      "8": "eight",
      "9": "nine",
      "+": "plus"
    };
    
    const mapping = lng === "en" ? englishDigits : germanDigits;
    return mapping[digit] || digit;
  }
  
  return digitWord;
}

/**
 * Converts a phone number to word representation
 * @param phoneNumber - The phone number to convert
 * @returns The phone number with digits converted to words
 */
function convertPhoneToWords(phoneNumber: string): string {
  return `. ${phoneNumber
    .split("")
    .map(char => {
      if (/\d/.test(char) || char === "+") {
        return digitToWord(char);
      }
      return "."; // Replace spaces, dashes, parentheses with dots
    })
    .filter(word => word !== ".") // Remove empty segments
    .join(", ")}.`;
}

export function normalizePhoneNumber(message: string): string {
  // Extract phone numbers from the message
  const phoneMatches = message.match(PHONE_REGEX);
  
  if (!phoneMatches) {
    return message;
  }
  
  let normalizedMessage = message;
  
  // Replace each phone number with its word representation
  phoneMatches.forEach(match => {
    // Clean up the phone number (remove excessive spaces, format, and trailing punctuation)
    const cleanPhone = match.replace(/\s+/g, " ").replace(/[.,;!?]+$/, "").trim();
    
    // Basic validation: should contain at least 7 digits
    const digitCount = (cleanPhone.match(/\d/g) || []).length;
    if (digitCount >= 7 && digitCount <= 15) {
      const wordifiedPhone = convertPhoneToWords(cleanPhone);
      normalizedMessage = normalizedMessage.replace(match, wordifiedPhone);
    }
  });

  return normalizedMessage;
}

const normalizeEmail = (message: string): string => {
  return message.replaceAll(".de" , " Punkt, D, E, ").replaceAll("@", " At, ");
}

const normalizeUrl = (message: string): string => {
  return message.replaceAll("/", " Schrägstrich, ")
}